import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError } from "../../lib/api";
import { Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function SAResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("No reset token found. Please request a new reset link.");
  }, [token]);

  const validatePassword = (p) => {
    if (p.length < 8) return "Password must be at least 8 characters.";
    if (!/\d/.test(p)) return "Password must contain at least one number.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const pwErr = validatePassword(newPassword);
    if (pwErr) { setError(pwErr); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/admin/auth/reset-password", { token, new_password: newPassword });
      setDone(true);
      toast.success("Password updated!");
      setTimeout(() => navigate("/admin/login"), 2500);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#080808]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-2xl font-black tracking-tight text-white mb-1">TabOn <span className="text-[#9D00FF]">Admin</span></div>
          <p className="text-sm text-[#A0A0A0]">Super Admin Portal</p>
        </div>

        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-7 shadow-2xl">
          {!token ? (
            <div className="flex flex-col items-center gap-4 text-center py-4">
              <AlertCircle size={40} className="text-red-400" />
              <div>
                <h2 className="text-xl font-bold mb-1 text-red-400">Invalid Link</h2>
                <p className="text-sm text-[#A0A0A0]">
                  This link has expired or is invalid. Please request a new reset link.
                </p>
              </div>
              <Link to="/admin/login" className="text-sm text-[#9D00FF] font-semibold hover:underline flex items-center gap-1">
                <ArrowLeft size={13} /> Back to Login
              </Link>
            </div>
          ) : done ? (
            <div className="flex flex-col items-center gap-4 text-center py-4">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                <CheckCircle size={26} className="text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-1">Password Updated!</h2>
                <p className="text-sm text-[#A0A0A0]">Redirecting to login…</p>
              </div>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold mb-1">Reset SA Password</h1>
              <p className="text-sm text-[#A0A0A0] mb-6">
                Set a new password. Min 8 characters with at least one number.
              </p>
              <form onSubmit={submit} className="flex flex-col gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">New Password</label>
                  <div className="relative">
                    <input
                      type={showNew ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      placeholder="Min 8 chars, 1 number"
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 pr-12 min-h-[44px] text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF]"
                    />
                    <button type="button" onClick={() => setShowNew(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]">
                      {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder="Re-enter password"
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 pr-12 min-h-[44px] text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF]"
                    />
                    <button type="button" onClick={() => setShowConfirm(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]">
                      {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />{error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 text-white font-semibold rounded-lg py-3 min-h-[44px] transition-all"
                >
                  {loading ? "Updating…" : "Set New Password"}
                </button>
              </form>
              <div className="mt-4 text-center">
                <Link to="/admin/login" className="flex items-center justify-center gap-1.5 text-sm text-[#A0A0A0] hover:text-white">
                  <ArrowLeft size={14} /> Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
